import React, {
  Fragment,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import "moment-timezone";
import "src/index.css";
import AuthService from "src/service/api";
import { ORDER_TYPE_KEYS } from "src/utils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import OrderNote from "src/components/Header/OrderNote";
import OrderHeader from "src/components/Order/OrderHeader";
import OrderProduct from "src/components/Order/OrderProduct";
import OrderCharges from "src/components/Order/OrderCharges";
import { groupByCategory } from "src/helper/groupByCategory";
import {
  buildPromotionAdjustmentsByLineUuid,
  collectOrderItems,
} from "src/helper/promotionItemAttribution";
import OrderFooter from "src/components/OrderFooter/OrderFooter";
import OrderTransactions from "src/components/Order/OrderTransactions";
import OrderPickUpInfo from "src/components/OrderPickUpInfo/OrderPickUpInfo";
import OrderDeliveryInfo from "src/components/OrderDeliveryInfo/OrderDeliveryInfo";
import RefundTransactions from "src/components/Order/RefundTransactions";
import { PRODUCT_STATUS } from "src/constants";
import NotAvailable from "../notFound/NotAvailable";
import Loading from "src/components/Loading/Loading";

const ORDER_STATUS_KEYS = {
  cancelled: "CANCELED",
  link_sent: "LINK_SENT",
};

export const Order = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const driver = searchParams.get("driver");
  const [orderData, setOrderData] = useState({});
  const [timeZoneId, setTimeZoneId] = useState("America/Los_Angeles");
  const productsData = groupByCategory(orderData?.categories);
  const [isOrderCancled, setIsOrderCancled] = useState(false);
  const [isItemsOpen, setIsItemsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { orderId, orderUuid, businessId, businessUuid } = useParams();

  // Extract parameters from query string when on /view route, memoized to
  // prevent a new object reference on every render (which would break
  // fetchData's closure and useEffect deps).
  const orderParams = useMemo(() => {
    if (location.pathname === "/view") {
      const urlParams = new URLSearchParams(location.search);
      return {
        orderId: urlParams.get("orderId"),
        orderUuid: urlParams.get("orderUuid") || urlParams.get("orderuuid"),
        businessId: urlParams.get("businessId"),
        businessUuid: urlParams.get("businessUuid"),
      };
    }
    return { orderId, orderUuid, businessId, businessUuid };
  }, [
    orderId,
    orderUuid,
    businessId,
    businessUuid,
    location.pathname,
    location.search,
  ]);

  const promotionAdjustmentsByUuid = useMemo(
    () =>
      buildPromotionAdjustmentsByLineUuid(
        collectOrderItems(orderData?.categories),
        orderData?.charges?.promotionDetails
      ),
    [orderData?.categories, orderData?.charges?.promotionDetails]
  );

  // Get the showVoidItemReceipt setting from business settings
  const showVoidItemReceipt =
    orderData?.metadata?.business?.settings?.showVoidItemReceipt ?? true;

  // Helper function to check if category has any visible items
  const hasVisibleItems = useMemo(() => {
    return (categoryItems) => {
      if (!categoryItems?.items) return false;

      return categoryItems.items.some((products) =>
        products.items.some((item) => {
          const isVoid = item?.status?.name === PRODUCT_STATUS.VOID;
          // Item is visible if it's not void, or if it's void and showVoidItemReceipt is true
          return !isVoid || (isVoid && showVoidItemReceipt);
        }),
      );
    };
  }, [showVoidItemReceipt]);

  // Filter categories that have visible items
  const visibleCategories = useMemo(() => {
    if (!productsData || Object.keys(productsData).length === 0) return {};

    const filtered = {};
    Object.keys(productsData).forEach((key) => {
      if (hasVisibleItems(productsData[key])) {
        filtered[key] = productsData[key];
      }
    });
    return filtered;
  }, [productsData, hasVisibleItems]);

  // Get the hideConfirmPayLater setting from business settings
  const hideConfirmPayLater =
    orderData?.metadata?.business?.settings?.hideConfirmPayLater ?? false;

  const showPayLetterButton = () => {
    // If hideConfirmPayLater is true, hide the button
    if (hideConfirmPayLater) {
      return false;
    }
    // Otherwise, show button when status is link_sent
    return orderData?.status?.name === ORDER_STATUS_KEYS.link_sent;
  };

  // Stable identifier used for the API call — derived here so fetchData
  // depends on a single primitive rather than the whole orderParams object.
  const fetchId = orderParams.orderUuid || orderParams.orderId;

  const fetchData = useCallback(async () => {
    try {
      const response = await AuthService.getOrderData(fetchId);
      if (response?.data?.data?.metadata?.business) {
        document.title = `Orders co | ${response?.data?.data?.metadata?.business?.companyName}`;
      } else {
        document.title = `Orders co`;
      }
      if (response) {
        setIsOrderCancled(
          response?.data?.data?.status?.name === ORDER_STATUS_KEYS.cancelled,
        );
        setOrderData(response.data.data || {});
        setTimeZoneId(
          response?.data?.data?.metadata?.business?.timezone?.timeZoneId ||
            response?.data?.data?.metadata?.business?.timezone ||
            "America/Los_Angeles",
        );
      }
      setIsLoading(false);
    } catch (e) {
      document.title = `Orders co`;
      console.log(e.response, "e");
      setOrderData({});
      setIsLoading(false);
      setTimeZoneId("America/Los_Angeles");
    }
  }, [fetchId]);

  const handleConfirmPayLetter = async () => {
    try {
      const response = await AuthService.confirmPayLetter(fetchId);
      if (response) {
        fetchData();
      }
    } catch (e) {
      console.log(e, "PayLetter Crashed");
    }
  };

  useEffect(() => {
    if (!fetchId) {
      navigate("/404");
      return;
    }
    fetchData();
  }, [fetchId, navigate, fetchData]);

  const businessFetchId = orderParams.businessUuid || orderParams.businessId;

  const handlePay = () => {
    if (driver === "true") {
      window.open(
        `${process.env.REACT_APP_PAYMENT_URL}?bId=${businessFetchId}&oId=${fetchId}&driver=${driver}`,
        "_blank",
      );
    } else {
      window.open(
        `${process.env.REACT_APP_PAYMENT_URL}?bId=${businessFetchId}&oId=${fetchId}`,
        "_blank",
      );
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (!isLoading && orderData && !Object.keys(orderData)?.length) {
    return <NotAvailable />;
  }

  return (
    <>
      {orderData && Object.keys(orderData)?.length > 0 && (
        <Box position="relative" id="receipt">
          {isOrderCancled && (
            <Box className="canceled-text-container">
              <Typography className="canceled-text">CANCELED</Typography>
            </Box>
          )}
          {driver === "true" && (
            <Box
              sx={{
                backgroundColor: "#ff8a00",
                color: "white",
                padding: "8px 16px",
                margin: "8px 0",
                borderRadius: "4px",
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              🛵 DRIVER VIEW
            </Box>
          )}
          <OrderHeader orderData={orderData} timeZoneId={timeZoneId} />
          {orderData?.deliveryInfo &&
          (orderData?.orderType === ORDER_TYPE_KEYS.delivery ||
            orderData?.orderType === ORDER_TYPE_KEYS.catering_delivery ||
            orderData?.orderType === ORDER_TYPE_KEYS.phone_order_delivery) ? (
            <OrderDeliveryInfo
              deliveryInfo={orderData?.deliveryInfo}
              customerInfo={orderData?.customerInfo}
              refCode={orderData?.metadata?.deliveryDetails?.refCode}
              driverInfo={orderData?.metadata?.driverInfo || null}
              orderUUid={orderData?.uuid}
              businessId={orderData?.metadata?.business?.id}
              businessUuid={orderData?.businessUuid}
            />
          ) : (
            <></>
          )}
          {(orderData?.customerInfo?.firstName ||
            orderData?.customerInfo?.phoneNumber) &&
          orderData?.orderType !== ORDER_TYPE_KEYS.delivery &&
          orderData?.orderType !== ORDER_TYPE_KEYS.catering_delivery &&
          orderData?.orderType !== ORDER_TYPE_KEYS.phone_order_delivery ? (
            <OrderPickUpInfo
              customerInfo={orderData?.customerInfo}
              refCode={orderData?.metadata?.deliveryDetails?.refCode}
            />
          ) : (
            <></>
          )}

          {orderData?.specialInstructions && (
            <OrderNote instructions={orderData?.specialInstructions} />
          )}
          <Box className="accordion">
            <Box
              className="accordion-header"
              aria-expanded={isItemsOpen}
              onClick={() => setIsItemsOpen(!isItemsOpen)}
            >
              <Box className="items">Items: (click to expand/collapse)</Box>
              <span
                style={{
                  display: "inline-block",
                  animation: "rotateOnLoad 2s ease-in-out 1",
                }}
                onAnimationEnd={(e) => {
                  e.target.style.animation = "none";
                }}
              >
                {isItemsOpen ? "-" : "+"}
              </span>
            </Box>
            <Box className={`items-container ${isItemsOpen ? "open" : ""}`}>
              {isItemsOpen && (
                <Box>
                  {Object.keys(visibleCategories)?.length > 0 ? (
                    Object.keys(visibleCategories)?.map((keys, index) => {
                      return (
                        <>
                          <Fragment key={keys}>
                            {visibleCategories[keys]?.categoryName && (
                              <>
                                {index !== 0 && <div className="separator" />}
                                <Box>
                                  <Typography
                                    style={{ fontWeight: 600, marginTop: 12 }}
                                    className="categoryName product-name-longer"
                                  >
                                    {visibleCategories[
                                      keys
                                    ].categoryName.toUpperCase()}
                                  </Typography>
                                </Box>
                              </>
                            )}
                          </Fragment>

                          {visibleCategories[keys]?.items?.map((products) =>
                            products?.items?.map((item) => {
                              return (
                                <OrderProduct
                                  key={item.uuid || item._id}
                                  product={item}
                                  charges={orderData?.charges}
                                  promotions={
                                    promotionAdjustmentsByUuid.get(item.uuid) ||
                                    []
                                  }
                                  showVoidItemReceipt={showVoidItemReceipt}
                                />
                              );
                            }),
                          )}
                        </>
                      );
                    })
                  ) : (
                    <Box
                      sx={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#666",
                        fontStyle: "italic",
                      }}
                    >
                      <Typography>
                        All items in this order have been voided and are hidden
                        from view.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          <hr className="line-header" />
          <table id="table">
            <tbody>
              <OrderCharges orderData={orderData} />
            </tbody>
          </table>
          {orderData?.metadata?.transactions?.length > 0 ? (
            <OrderTransactions orderData={orderData} />
          ) : (
            <hr className="line-header" />
          )}
          {orderData?.metadata?.transactions?.length > 0 ? (
            <RefundTransactions orderData={orderData} />
          ) : (
            <></>
          )}
          <OrderFooter
            customerInfo={orderData?.customerInfo}
            orderData={orderData}
          />
          {showPayLetterButton() ? (
            <>
              <hr className="line-header" />
              <Box className="flex-center-gap">
                <Button
                  variant="outlined"
                  className="pay-letter"
                  disabled={isOrderCancled}
                  onClick={handleConfirmPayLetter}
                >
                  Confirm pay later
                </Button>
                {orderData?.charges?.balanceDue > 0 && (
                  <Button
                    className="pay"
                    disabled={isOrderCancled}
                    onClick={handlePay}
                  >
                    PAY
                  </Button>
                )}
              </Box>
            </>
          ) : (
            <>
              {orderData?.charges?.balanceDue > 0 && (
                <>
                  <hr className="line-header" />
                  <Button
                    className="pay"
                    disabled={isOrderCancled}
                    onClick={handlePay}
                  >
                    PAY
                  </Button>
                </>
              )}
            </>
          )}
        </Box>
      )}
    </>
  );
};
